import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import { ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { orve } from '../shared/orve';
import { GREEN_YELLOW_RED_COLORS } from '../../client-dashboard/candidate-scoring-util';
import { getCandidateValuesText, getScoreColors } from '../shared/report-util';
import { useAuth } from '../../../providers/auth-provider';
import { REPORT_TYPE } from '../../../utils/constants';

function LeaderReportSummary({ candidate, speedometers, isDevelopment = false, reportType = null }) {
  const { isSelect } = useAuth();
  const [overall, setOverall] = useState(0);
  const [performanceLoyaltyScores, setPerformanceLoyaltyScores] = useState('');

  const getOverallLegend = useCallback(() => {
    switch (performanceLoyaltyScores) {
      case 'greenGreen':
        return (
          <div>
            <div>High Overall</div>
            <div>Potential</div>
          </div>
        );
      case 'greenYellow':
        return (
          <div>
            <div>Disengaged</div>
            <div>Great Performer</div>
          </div>
        );
      case 'greenRed':
        return (
          <div>
            <div>Apathetic</div>
            <div>Great Performer</div>
          </div>
        );
      case 'yellowGreen':
        return (
          <div>
            <div>Loyal</div>
            <div>Good Performer</div>
          </div>
        );
      case 'yellowYellow':
        return (
          <div>
            <div>Disengaged</div>
            <div>Good Performer</div>
          </div>
        );
      case 'yellowRed':
        return (
          <div>
            <div>Apathetic</div>
            <div>Good Performer</div>
          </div>
        );
      case 'redGreen':
        return (
          <div>
            <div>Loyal</div>
            <div>Poor Performer</div>
          </div>
        );
      case 'redYellow':
        return (
          <div>
            <div>Disengaged</div>
            <div>Poor Performer</div>
          </div>
        );
      case 'redRed':
        return (
          <div>
            <div>Apathetic</div>
            <div>Low Potential</div>
          </div>
        );
      default:
        return (
          <div>
            <div>Do Not</div>
            <div>Recommend</div>
          </div>
        );
    }
  }, [performanceLoyaltyScores]);

  const getOverallColors = useCallback(() => {
    switch (performanceLoyaltyScores) {
      case 'greenGreen':
        return (
          <>
            <Cell fill={GREEN_YELLOW_RED_COLORS.highlyRecommend.primary} />
            <Cell fill={GREEN_YELLOW_RED_COLORS.highlyRecommend.secondary} />
          </>
        );
      case 'greenYellow':
      case 'yellowGreen':
        return (
          <>
            <Cell fill={GREEN_YELLOW_RED_COLORS.recommend.primary} />
            <Cell fill={GREEN_YELLOW_RED_COLORS.recommend.secondary} />
          </>
        );
      case 'greenRed':
      case 'yellowYellow':
      case 'redGreen':
        return (
          <>
            <Cell fill={GREEN_YELLOW_RED_COLORS.recommendWithCaution.primary} />
            <Cell fill={GREEN_YELLOW_RED_COLORS.recommendWithCaution.secondary} />
          </>
        );
      case 'yellowRed':
      case 'redYellow':
      case 'redRed':
        return (
          <>
            <Cell fill={GREEN_YELLOW_RED_COLORS.doNotRecommend.primary} />
            <Cell fill={GREEN_YELLOW_RED_COLORS.doNotRecommend.secondary} />
          </>
        );
      default:
        return (
          <>
            <Cell fill={GREEN_YELLOW_RED_COLORS.doNotRecommend.primary} />
            <Cell fill={GREEN_YELLOW_RED_COLORS.doNotRecommend.secondary} />
          </>
        );
    }
  }, [performanceLoyaltyScores]);

  // TODO Use existing methods in candidate-scoring-util.js
  useEffect(() => {
    if (candidate) {
      if (candidate.percentiles.performance >= 66) {
        if (candidate.percentiles.loyalty >= 66) {
          setPerformanceLoyaltyScores('greenGreen');
          return setOverall(100);
        }
        if (candidate.percentiles.loyalty >= 33) {
          setPerformanceLoyaltyScores('greenYellow');
          return setOverall(75);
        }
        if (candidate.percentiles.loyalty < 33) {
          setPerformanceLoyaltyScores('greenRed');
          return setOverall(75);
        }
      }

      if (candidate.percentiles.performance >= 33) {
        if (candidate.percentiles.loyalty >= 66) {
          setPerformanceLoyaltyScores('yellowGreen');
          return setOverall(75);
        }
        if (candidate.percentiles.loyalty >= 33) {
          setPerformanceLoyaltyScores('yellowYellow');
          return setOverall(50);
        }
        if (candidate.percentiles.loyalty < 33) {
          setPerformanceLoyaltyScores('yellowRed');
          return setOverall(25);
        }
      }

      if (candidate.percentiles.loyalty >= 66) {
        setPerformanceLoyaltyScores('redGreen');
        return setOverall(50);
      }
      if (candidate.percentiles.loyalty >= 33) {
        setPerformanceLoyaltyScores('redYellow');
        return setOverall(25);
      }
      setPerformanceLoyaltyScores('redRed');
      return setOverall(25);
    }
  }, [candidate]);

  return (
    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
      {candidate && (
        <>
          {speedometers && (
            <>
              <Row className="justify-content-center pt-4">Predictive Summary</Row>
              <Row className="justify-content-center w-25 mx-auto pt-3">
                <hr style={{ borderTop: '2px solid' }} />
              </Row>
              <Row className="justify-content-center pt-3 pb-3">
                <Stack direction="horizontal" className="justify-content-evenly d-none d-md-flex">
                  {reportType !== REPORT_TYPE.development && (
                    <Stack className="align-items-center">
                      <div className="text-bolder text-larger">Potential</div>
                      <div style={{ height: '5em', width: '10em' }}>
                        <ResponsiveContainer width="100%" aspect={1}>
                          <PieChart>
                            <Pie
                              data={[{ value: overall }, { value: 100 - overall }]}
                              dataKey="value"
                              innerRadius={50}
                              outerRadius={70}
                              startAngle={180}
                              endAngle={0}
                            >
                              {getOverallColors()}
                            </Pie>
                            <Legend
                              content={getOverallLegend()}
                              verticalAlign="middle"
                              className="candidate-profile-recommend-legend"
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </Stack>
                  )}
                  <Stack className="align-items-center">
                    <div className="text-bolder text-larger">Performance</div>
                    <div style={{ height: '5em', width: '10em' }}>
                      <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                          <Pie
                            data={[
                              { value: candidate.percentiles.performance },
                              { value: 100 - candidate.percentiles.performance },
                            ]}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            startAngle={180}
                            endAngle={0}
                          >
                            {getScoreColors(candidate.percentiles.performance, REPORT_TYPE.development)}
                          </Pie>
                          <Legend
                            content={<div>{`${candidate.percentiles.performance}%`}</div>}
                            verticalAlign="middle"
                            className="candidate-profile-summary-legend"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Stack>
                  <Stack className="align-items-center">
                    <div className="text-bolder text-larger">Loyalty</div>
                    <div style={{ height: '5em', width: '10em' }}>
                      <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                          <Pie
                            data={[
                              { value: candidate.percentiles.loyalty },
                              { value: 100 - candidate.percentiles.loyalty },
                            ]}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            startAngle={180}
                            endAngle={0}
                          >
                            {getScoreColors(candidate.percentiles.loyalty, REPORT_TYPE.development)}
                          </Pie>
                          <Legend
                            content={<div>{`${candidate.percentiles.loyalty}%`}</div>}
                            verticalAlign="middle"
                            className="candidate-profile-summary-legend"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Stack>
                </Stack>
                <Stack direction="vertical" className="justify-content-evenly d-md-none" gap={5}>
                  <Stack className="align-items-center">
                    <div className="text-bolder text-larger">Recommendation</div>
                    <div style={{ height: '5em', width: '10em' }}>
                      <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                          <Pie
                            data={[{ value: overall }, { value: 100 - overall }]}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            startAngle={180}
                            endAngle={0}
                          >
                            {getOverallColors()}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Stack>
                  <Stack className="align-items-center">
                    <div className="text-bolder text-larger">Performance</div>
                    <div style={{ height: '5em', width: '10em' }}>
                      <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                          <Pie
                            data={[
                              { value: candidate.percentiles.performance },
                              { value: 100 - candidate.percentiles.performance },
                            ]}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            startAngle={180}
                            endAngle={0}
                          >
                            {getScoreColors(candidate.percentiles.performance, REPORT_TYPE.development)}
                          </Pie>
                          <Legend
                            content={<div>{`${candidate.percentiles.performance}%`}</div>}
                            verticalAlign="middle"
                            className="candidate-profile-summary-legend"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Stack>
                  <Stack className="align-items-center">
                    <div className="text-bolder text-larger">Loyalty</div>
                    <div style={{ height: '5em', width: '10em' }}>
                      <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                          <Pie
                            data={[
                              { value: candidate.percentiles.loyalty },
                              { value: 100 - candidate.percentiles.loyalty },
                            ]}
                            dataKey="value"
                            innerRadius={50}
                            outerRadius={70}
                            startAngle={180}
                            endAngle={0}
                          >
                            {getScoreColors(candidate.percentiles.loyalty, REPORT_TYPE.development)}
                          </Pie>
                          <Legend
                            content={<div>{`${candidate.percentiles.loyalty}%`}</div>}
                            verticalAlign="middle"
                            className="candidate-profile-summary-legend"
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Stack>
                </Stack>
              </Row>
            </>
          )}
          <Row className="justify-content-center pt-4">Leadership Summary</Row>
          <Row className="justify-content-center w-25 mx-auto pt-3">
            <hr style={{ borderTop: '2px solid' }} />
          </Row>
          <Row>
            <div className="text-bolder mx-auto pt-2 pb-2 candidate-profile-overall-content">
              {isDevelopment ? (
                <>
                  Based on the assessments that you completed, the following report provides detailed scoring on your
                  personality, acumen,{` ${getCandidateValuesText(isSelect())} `}fit, experience, judgment and
                  competencies. The last part of the report provides targeted developmental recommendations based on
                  your assessment scores.
                </>
              ) : (
                orve(candidate.percentiles.performance, candidate.percentiles.loyalty, false)
              )}
            </div>
          </Row>
        </>
      )}
    </Container>
  );
}

export default LeaderReportSummary;
